// Modal wrapper for the basic-details edit flow — opened from the pipeline
// row-dropdown "Edytuj podstawowe" action (see usePipelineActions).
//
// Refactor of `src/pages/transactions/components/modals/BasicDetailsModal.tsx`
// (+ its BasicDetailsForm sibling): legacy `src/components/form/SectionHeader`,
// legacy `@ds/tokens/tokens`, and raw AntD `Modal` swapped for `@ds/primitives/Modal`
// + `@ds/display/Eyebrow` + `@ds/display/Pill` + tokens from `@ds/tokens/palette`.
// Every Form.Item, effect, watcher, validation rule, and submit semantic from
// the legacy is preserved verbatim.
//
// PRESERVED FIELDS (Form.Items, in render order):
//   product_id, external_id, load_place_company_id, unload_place_company_id,
//   recycling_process_code, load_commercial_company_id, unload_commercial_company_id,
//   exporter_company_id, importer_company_id, logistics_employee_id,
//   load_sales_employee_id, unload_sales_employee_id

import { CopyOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useCreate, useInvalidate, useOne, useUpdate } from "@refinedev/core";
import { useNavigate } from "react-router";
import { Form } from "antd";
import { Input } from "@ds/editable/Input";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Eyebrow } from "@ds/display/Eyebrow";
import { Pill } from "@ds/display/Pill";
import { TextValue } from "@ds/display/TextValue";
import { Modal } from "@ds/primitives/Modal";
import { SchemaFormItem } from "@ds/forms/SchemaFormItem";
import { FormFieldTestIdContext } from "@ds/forms/formTestIdContext";
import { Box } from "@ds/layout/Box";
import { Grid } from "@ds/layout/Grid";
import { Stack } from "@ds/layout/Stack";

import { buildDuplicateValues } from "./buildDuplicateValues";
import { transactionPresentation } from "../transactionConfig";
import { toListColumn } from "@ds/config/presentationRefs";
import { useCurrentEmployee } from "../../../contexts/useCurrentEmployee";
import { transactionSvc } from "@services/index";
import type { Transaction } from "@shared/types/transaction";

// ─── Presentation field refs ──────────────────────────────────────────────
// Lazy getters: `transactionPresentation` lives in transactionConfig and is
// reached through import cycles — reading it at module-eval TDZs. Each getter
// defers to render. See feedback_presentation_tdz_cycles.

const txFields = {
  get product()           { return toListColumn(transactionPresentation.product_id); },
  get loadPlace()         { return toListColumn(transactionPresentation.load_place_company_id); },
  get unloadPlace()       { return toListColumn(transactionPresentation.unload_place_company_id); },
  get loadCommercial()    { return toListColumn(transactionPresentation.load_commercial_company_id); },
  get unloadCommercial()  { return toListColumn(transactionPresentation.unload_commercial_company_id); },
  get exporter()          { return toListColumn(transactionPresentation.exporter_company_id); },
  get importer()          { return toListColumn(transactionPresentation.importer_company_id); },
  get logistics()         { return toListColumn(transactionPresentation.logistics_employee_id); },
  get loadSales()         { return toListColumn(transactionPresentation.load_sales_employee_id); },
  get unloadSales()       { return toListColumn(transactionPresentation.unload_sales_employee_id); },
};

// ─── Public API ──────────────────────────────────────────────────────────

export type BasicDetailsModalMode = "edit" | "duplicate" | "create";

export interface BasicDetailsModalProps {
  transactionId: number | null;
  /** Mode. `"edit"` patches the source record (default); `"duplicate"`
   *  pre-fills the form from the source but creates a new transaction on
   *  save and navigates to it. `"create"` opens an empty form and creates
   *  a new transaction on save — `transactionId` must be `null`. */
  mode?: BasicDetailsModalMode;
  /** Explicit open flag. Required when `mode === "create"` since there is no
   *  source `transactionId` to derive visibility from. For `"edit"` /
   *  `"duplicate"` the modal is open whenever `transactionId !== null`. */
  open?: boolean;
  onClose: () => void;
  /** Fired after a successful save. In `"duplicate"` / `"create"` mode the
   *  new tx id is passed so callers can refresh lists referencing it. */
  onSaved: (newId?: number) => void;
}

// ─── Form handle (used by the modal shell to drive imperative save) ──────

interface BasicDetailsFormHandle {
  save: () => Promise<void>;
  discard: () => void;
}

// ─── RPC helper — resolve sales employee for (company, product, side) ────
// Mirrors the legacy helper verbatim. Wraps `resolve_sales_employee` RPC.

const resolveSalesEmployee = (
  companyId: number,
  productId: number | null,
  side: "load" | "unload",
): Promise<number | null> => transactionSvc.resolveSalesEmployee(companyId, productId, side);

// ─── Form body ────────────────────────────────────────────────────────────
//
// Inner form component. The modal shell renders this once `transactionId`
// is non-null. Hydrates from `transactions_with_checks` (carries product
// waste_code + rule.annex7_generated_required, both needed to decide
// whether `recycling_process_code` is required, mirroring the DB
// `recycling_process_set` logic).

interface BasicDetailsFormProps {
  transactionId: number | null;
  active:        boolean;
  mode:          BasicDetailsModalMode;
  /** In duplicate / create mode, receives the newly-created transaction's id. */
  onSuccess:     (newId?: number) => void;
  onDirtyChange: (dirty: boolean) => void;
}

// The scalar columns this modal edits (see the Form.Items below). Transaction's
// fetched relations are never form fields, and rc-field-form's RecursivePartial
// cannot express their `unknown[]` members — so the form value type covers the
// edited columns only.
type BasicDetailsValues = Partial<Pick<Transaction,
  | "product_id" | "external_id" | "recycling_process_code"
  | "load_place_company_id" | "unload_place_company_id"
  | "load_commercial_company_id" | "unload_commercial_company_id"
  | "exporter_company_id" | "importer_company_id"
  | "logistics_employee_id" | "load_sales_employee_id" | "unload_sales_employee_id"
>>;

const BasicDetailsForm = forwardRef<BasicDetailsFormHandle, BasicDetailsFormProps>(({
  transactionId, active, mode, onSuccess, onDirtyChange,
}, ref) => {
  const { t } = useTranslation();
  const { hasRole: _hasRole } = useCurrentEmployee();
  const [form] = Form.useForm<BasicDetailsValues>();
  const { mutateAsync: updateTransaction } = useUpdate();
  const { mutateAsync: createTransaction } = useCreate();
  const invalidate = useInvalidate();

  // Pipeline + queues READ from the `transactions_with_checks` view; the
  // mutations below WRITE to `transactions`. Refine auto-invalidates only the
  // write resource, so without this the edited row (e.g. a changed logistician)
  // stays stale in those list views until a manual reload.
  const invalidateTxViews = () => {
    invalidate({ resource: "transactions",             invalidates: ["list", "detail"] });
    invalidate({ resource: "transactions_with_checks", invalidates: ["list"] });
  };
  const navigate = useNavigate();
  const hydrated = useRef(false);
  const skipNextEffectRef = useRef(false);
  const isDuplicate = mode === "duplicate";

  // "Latest value, don't retrigger" mirrors. `onDirtyChange` is passed as an
  // inline arrow by the modal shell (new identity every render) and `active`
  // is a pure guard — listing either as a real dependency below would re-run
  // the hydration / sales-resolution effects on every parent render and
  // clobber whatever the operator has already typed.
  const onDirtyChangeRef = useRef(onDirtyChange);
  const activeRef = useRef(active);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
    activeRef.current = active;
  });

  const { query: txQuery } = useOne<Transaction & {
    annex_na: boolean;
    product:  { id: number; name?: string | null; waste_code: string | null } | null;
    rule:     { annex7_generated_required: boolean | null } | null;
  }>({
    resource: "transactions_with_checks",
    id:       transactionId ?? 0,
    queryOptions: { enabled: active && !!transactionId && mode !== "create" },
    meta: {
      // Joins for `extraOption` on every EntitySelect — guarantees the
      // current value renders with its proper name even when role/category
      // filters would otherwise hide it, or before the list query lands.
      select: [
        "*",
        "product:product_id(id,name,waste_code)",
        "rule:transaction_rules!rule_id(annex7_generated_required)",
        "load_place_company:load_place_company_id(id,short_name)",
        "unload_place_company:unload_place_company_id(id,short_name)",
        "load_commercial_company:load_commercial_company_id(id,short_name)",
        "unload_commercial_company:unload_commercial_company_id(id,short_name)",
        "exporter_company:exporter_company_id(id,short_name)",
        "importer_company:importer_company_id(id,short_name)",
        "logistics_employee:logistics_employee_id(id,full_name)",
        "load_sales_employee:load_sales_employee_id(id,full_name)",
        "unload_sales_employee:unload_sales_employee_id(id,full_name)",
      ].join(", "),
    },
  });
  const tx = txQuery.data?.data;

  // Pre-resolved options for every entity-typed Form.Item. Keeps the AntD
  // Select from rendering a raw ID when the list query is still loading or
  // when a role/category filter excludes the currently-stored record.
  const productOpt = tx?.product?.id
    ? { value: tx.product.id, label: tx.product.name ?? `#${tx.product.id}` }
    : null;
  const loadPlaceOpt = tx?.load_place_company?.id
    ? { value: tx.load_place_company.id, label: tx.load_place_company.short_name }
    : null;
  const unloadPlaceOpt = tx?.unload_place_company?.id
    ? { value: tx.unload_place_company.id, label: tx.unload_place_company.short_name }
    : null;
  const loadCommercialOpt = tx?.load_commercial_company?.id
    ? { value: tx.load_commercial_company.id, label: tx.load_commercial_company.short_name }
    : null;
  const unloadCommercialOpt = tx?.unload_commercial_company?.id
    ? { value: tx.unload_commercial_company.id, label: tx.unload_commercial_company.short_name }
    : null;
  const exporterOpt = tx?.exporter_company?.id
    ? { value: tx.exporter_company.id, label: tx.exporter_company.short_name }
    : null;
  const importerOpt = tx?.importer_company?.id
    ? { value: tx.importer_company.id, label: tx.importer_company.short_name }
    : null;
  const logisticsOpt = tx?.logistics_employee?.id
    ? { value: tx.logistics_employee.id, label: tx.logistics_employee.full_name ?? `#${tx.logistics_employee.id}` }
    : null;
  const loadSalesOpt = tx?.load_sales_employee?.id
    ? { value: tx.load_sales_employee.id, label: tx.load_sales_employee.full_name ?? `#${tx.load_sales_employee.id}` }
    : null;
  const unloadSalesOpt = tx?.unload_sales_employee?.id
    ? { value: tx.unload_sales_employee.id, label: tx.unload_sales_employee.full_name ?? `#${tx.unload_sales_employee.id}` }
    : null;

  // Mirrors DB recycling_process_set logic exactly:
  // required unless annex_na (domestic), no waste_code, or rule disables annex.
  const isRecyclingProcessRequired =
    !!tx &&
    !tx.annex_na &&
    !!tx.product?.waste_code &&
    tx.rule?.annex7_generated_required !== false;

  useEffect(() => {
    if (tx && active) {
      if (skipNextEffectRef.current) { skipNextEffectRef.current = false; return; }
      // Duplicate mode prefills carry-over fields but blanks the
      // per-shipment identifiers, dates, and logistician — see
      // buildDuplicateValues for the full reset + rationale.
      const values = isDuplicate ? buildDuplicateValues(tx) : tx;
      form.setFieldsValue(values);
      hydrated.current = true;
      onDirtyChangeRef.current(false);
    }
  }, [tx, active, isDuplicate, form]);

  const loadCompanyId   = Form.useWatch("load_commercial_company_id",   form);
  const unloadCompanyId = Form.useWatch("unload_commercial_company_id", form);
  const productId       = Form.useWatch("product_id",                   form);

  useEffect(() => {
    if (!loadCompanyId || !activeRef.current || !hydrated.current) return;
    resolveSalesEmployee(loadCompanyId, productId || null, "load").then((empId) => {
      if (empId) form.setFieldValue("load_sales_employee_id", empId);
    });
  }, [loadCompanyId, productId, form]);

  useEffect(() => {
    if (!unloadCompanyId || !activeRef.current || !hydrated.current) return;
    resolveSalesEmployee(unloadCompanyId, productId || null, "unload").then((empId) => {
      if (empId) form.setFieldValue("unload_sales_employee_id", empId);
    });
  }, [unloadCompanyId, productId, form]);

  const handleSave = async () => {
    skipNextEffectRef.current = true;
    const values = await form.validateFields();
    if (isDuplicate || mode === "create") {
      // `appTransforms.stripTransaction` (registered on the `transactions`
      // resource) drops relation snapshots + generated columns before the
      // POST hits Postgres, so we hand the raw form values to useCreate.
      const created = await createTransaction({
        resource: "transactions",
        values:   values as Record<string, unknown>,
      });
      const newId = (created?.data as { id?: number } | undefined)?.id;
      invalidateTxViews();
      onDirtyChange(false);
      onSuccess(newId);
      if (newId) navigate(`/transactions/${newId}`);
      return;
    }
    if (transactionId == null) return;
    await updateTransaction({ resource: "transactions", id: transactionId, values });
    invalidateTxViews();
    onDirtyChange(false);
    onSuccess();
  };

  const handleDiscard = () => {
    if (tx) form.setFieldsValue(tx);
    else form.resetFields();
    onDirtyChange(false);
  };

  // Same "latest value, don't retrigger" mirror as `onDirtyChangeRef` above:
  // both handlers close over props that change identity every parent render,
  // so the exposed handle delegates through refs instead of being rebuilt.
  // The sync lives in its own effect (declared BEFORE the handle) so it is
  // committed by the time the shell can call save()/discard().
  const handleSaveRef    = useRef(handleSave);
  const handleDiscardRef = useRef(handleDiscard);
  useEffect(() => {
    handleSaveRef.current    = handleSave;
    handleDiscardRef.current = handleDiscard;
  });

  useImperativeHandle(ref, () => ({
    save:    () => handleSaveRef.current(),
    discard: () => handleDiscardRef.current(),
  }), []);

  return (
    <FormFieldTestIdContext.Provider value="basic-details-form">
    <Form form={form} layout="vertical" onValuesChange={() => onDirtyChange(true)}>
      {/* ── PRODUKT + MIEJSCA ──────────────────────────────────────────── */}
      <SectionEyebrow>
        {t("transactions.sections.basic_info", "Produkt i miejsca")}
      </SectionEyebrow>

      <Grid columns="main-side" gap="md">
        {/* product — schema declares `entity-select kind="product"` +
            required. extraOption pre-resolved from the hydrate query's
            joined product. */}
        <SchemaFormItem
          field={txFields.product}
          extraOption={productOpt}
        />
        {/* external_id kept inline — needs `allowClear` which the schema's
            generic text variant doesn't expose. Operators clear an order
            number occasionally; the × affordance is load-bearing. */}
        <Form.Item name="external_id" label={t("transactions.fields.external_id")}>
          <Input allowClear testId="basic-details-form-external_id" />
        </Form.Item>
      </Grid>

      <Stack gap="md">
        {/* Load + unload place — schema declares entity-select with
            category filter ("waste_generator" / "recycler"), placeholder,
            and required. extraOption per-call from the join. */}
        <SchemaFormItem
          field={txFields.loadPlace}
          extraOption={loadPlaceOpt}
        />
        <SchemaFormItem
          field={txFields.unloadPlace}
          extraOption={unloadPlaceOpt}
        />
      </Stack>

      {/* recycling_process_code — schema now declares it as
          `entity-select kind="recyclingProcess"`. Disabled in this form
          (set by upstream process; operator sees but can't edit here).
          Conditional `required` is form-context-specific so it stays as
          a per-call override. */}
      <SchemaFormItem
        field={toListColumn(transactionPresentation.recycling_process_code)}
        disabled
        required={isRecyclingProcessRequired}
      />

      {/* priority — boolean flag rendered as a Switch (via the schema's
          `edit.field.type: "boolean"`). Editable in create / duplicate /
          edit; the duplicate form pre-fills the source value. */}
      <SchemaFormItem field={toListColumn(transactionPresentation.priority)} />

      {/* ── DATY ───────────────────────────────────────────────────────── */}
      <SectionEyebrow>
        {t("transactions.sections.dates", "Daty")}
      </SectionEyebrow>

      {/* Dates + times — all schema-driven. Labels + widgets come from
          transactionSchema; the registry dispatches `type:"date"` →
          DatePickerField and `type:"time"` → TimePickerField. */}
      <SchemaFormItem field={toListColumn(transactionPresentation.pickup_date_from)} />

      <Grid columns="main-side" gap="md">
        <SchemaFormItem field={toListColumn(transactionPresentation.load_date)} />
        <SchemaFormItem field={toListColumn(transactionPresentation.load_time)} />
      </Grid>

      <Grid columns="main-side" gap="md">
        <SchemaFormItem field={toListColumn(transactionPresentation.unload_date)} />
        <SchemaFormItem field={toListColumn(transactionPresentation.unload_time)} />
      </Grid>

      {/* ── STRONY HANDLOWE — demoted, optional block ──────────────────── */}
      <MutedBlock
        title={t("transactions.sections.commercial_parties", "Strony handlowe")}
        hint={t("transactions.form.parties_hint", "Wypełnij gdy różne od miejsc załadunku / rozładunku.")}
      >
        <Stack gap="md">
          {/* Commercial parties — schema declares `requireNip` (must be
              NIP-bearing for invoice generation). */}
          <SchemaFormItem
            field={txFields.loadCommercial}
            extraOption={loadCommercialOpt}
          />
          <SchemaFormItem
            field={txFields.unloadCommercial}
            extraOption={unloadCommercialOpt}
          />
        </Stack>
        <Stack gap="md">
          {/* Customs / legal parties — schema declares category filter
              ("exporter" / "importer"). */}
          <SchemaFormItem
            field={txFields.exporter}
            extraOption={exporterOpt}
          />
          <SchemaFormItem
            field={txFields.importer}
            extraOption={importerOpt}
          />
        </Stack>
      </MutedBlock>

      {/* ── OSOBY ODPOWIEDZIALNE ───────────────────────────────────────── */}
      <SectionEyebrow>
        {t("transactions.sections.responsible_people", "Osoby odpowiedzialne")}
      </SectionEyebrow>

      <Grid columns={3} gap="md">
        {/* Employees — schema declares role filter
            ("logistics" / "sales"). */}
        <SchemaFormItem
          field={txFields.logistics}
          extraOption={logisticsOpt}
        />
        <SchemaFormItem
          field={txFields.loadSales}
          extraOption={loadSalesOpt}
        />
        <SchemaFormItem
          field={txFields.unloadSales}
          extraOption={unloadSalesOpt}
        />
      </Grid>
    </Form>
    </FormFieldTestIdContext.Provider>
  );
});

BasicDetailsForm.displayName = "BasicDetailsForm";

// ─── Section helpers ─────────────────────────────────────────────────────
//
// Two tiny inline JSX helpers that replace the legacy SectionHeader /
// MutedSection from src/components/form/SectionHeader.tsx. The Eyebrow
// primitive drives the typography; the surrounding chrome (hairline
// divider, soft-surface card) is the small JSX kept inline per task
// guidance ("Section header is small JSX — fine to inline").

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Box size="sm" divider="bottom">
      <Eyebrow>{children}</Eyebrow>
    </Box>
  );
}

function MutedBlock({
  title, hint, children,
}: { title: React.ReactNode; hint?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box tone="muted" border shape="rounded-lg" size="md">
      <Stack gap="sm">
        <Eyebrow>{title}</Eyebrow>
        {hint && (
          <TextValue as="div" size="sm" tone="faint" lineHeight="normal">
            {hint}
          </TextValue>
        )}
        {children}
      </Stack>
    </Box>
  );
}

// ─── Modal shell ─────────────────────────────────────────────────────────

export function BasicDetailsModal({
  transactionId, onClose, onSaved, mode = "edit", open: openProp,
}: BasicDetailsModalProps) {
  const { t } = useTranslation();
  const formRef = useRef<BasicDetailsFormHandle>(null);
  const [saving, setSaving] = useState(false);
  const isDuplicate = mode === "duplicate";
  const isCreate   = mode === "create";

  // Visibility: create mode uses the explicit `open` prop; edit/duplicate
  // derive it from whether `transactionId` is non-null.
  const isOpen = isCreate ? (openProp ?? false) : transactionId !== null;

  // Light fetch for the title pill — only relevant in edit/duplicate modes.
  const { query: txQuery } = useOne<Pick<Transaction, "id" | "reference_number">>({
    resource: "transactions",
    id:       transactionId ?? 0,
    queryOptions: { enabled: !!transactionId && !isCreate },
    meta: { select: "id, reference_number" },
  });
  const ref = txQuery.data?.data?.reference_number;

  const icon     = isCreate ? <PlusOutlined /> : isDuplicate ? <CopyOutlined /> : <EditOutlined />;
  const iconTone = isCreate ? "green" : isDuplicate ? "purple" : "blue";
  const title    = isCreate
    ? t("transactions.titles.create", "Nowa transakcja")
    : isDuplicate
      ? t("transactions.titles.duplicate", "Duplikuj transakcję")
      : t("transactions.titles.edit",      "Edytuj transakcję");

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      await formRef.current?.save();
    } finally {
      setSaving(false);
    }
  }, [saving]);

  const handleSuccess = useCallback((newId?: number) => {
    onSaved(newId);
    onClose();
  }, [onSaved, onClose]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      icon={icon}
      iconTone={iconTone}
      title={title}
      pills={ref ? (
        <Pill tone={isDuplicate ? "purple" : "blue"} size="sm">
          <TextValue mono as="span">{ref}</TextValue>
        </Pill>
      ) : undefined}
      width={720}
      testId="basic-details-modal"
      body={
        isOpen ? (
          <BasicDetailsForm
            ref={formRef}
            transactionId={transactionId}
            active
            mode={mode}
            onSuccess={handleSuccess}
            onDirtyChange={() => {}}
          />
        ) : null
      }
      actions={[
        {
          id:        "cancel",
          label:     t("common.actions.cancel"),
          variant:   "default",
          disabled:  saving,
          onTrigger: onClose,
        },
        {
          id:        "save",
          label:     t("common.actions.save"),
          variant:   "primary",
          loading:   saving,
          onTrigger: handleSave,
        },
      ]}
    />
  );
}
